import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  createSession,
  getActiveSession,
  getInventoryPosConfig,
  getMachineDetails,
  logoutSession,
  tableLayoutAvailability,
} from "../../../../../../config/Services";
import { setInLocalStorage } from "../../../../../../utils/localStorageHelpers";
import EnvConfig from "../../../../../../config/EnvConfig";
import { getStoreCurrencySymbol } from "../../../../../../utils/currency";
import {
  addLicense,
  addadvance,
  addmanagerpin,
  addmanageruidx,
  addpin,
} from "../../../all-products/redux/Actions/SessionActions";
import { getMobilePos } from "../../../all-products/redux/Reducers/paginationReducer";
import { getAmount, getLicense } from "../../../all-products/redux/Reducers/sessionReducer";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import useGetDevice from "../../../hooks/useGetDevice";
import PalletLogo from "../../../../../../assets/images/Pallet-logo-blue-transparent (2).png";
import PrintIcon from "@mui/icons-material/Print";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import WifiIcon from "@mui/icons-material/Wifi";
import RefreshIcon from "@mui/icons-material/Refresh";
import LogoutIcon from "@mui/icons-material/Logout";
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import { emit } from "react-native-react-bridge/lib/web";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import usePrint from "../../../hooks/usePrint";
import AlertAudio from "../../../../../../assets/audio/order-alert2.mp3";
import "./indexV2.css";

export const OpeningV2 = () => {
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  const [amountValue, setAmountvalue] = useState("");
  const retailUserDetails = JSON.parse(localStorage.getItem("retailUserDetails") || "{}");
  const roles = retailUserDetails.roles || [];
  const uidx = retailUserDetails?.uidx;
  const isPosManager = roles.includes("POS_MANAGER");
  const dispatch = useDispatch();
  const amount = useSelector(getAmount);
  const orgId = localStorage.getItem("orgId");
  const contextId = localStorage.getItem("locId");
  const locName = localStorage.getItem("LocName") || "Location";
  const retailType = localStorage.getItem("retailType") || "";
  const [loader, setLoader] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [managerLoader, setManagerLoader] = useState(false);
  const [focusedButton, setFocusedButton] = useState("pos");
  const selectedLicense = useSelector(getLicense);
  const daySessionId = localStorage.getItem("daySessionId");
  const isMobilePos = useSelector(getMobilePos);
  const userName = retailUserDetails?.firstName + " " + retailUserDetails?.secondName;
  const isMobile = useGetDevice();
  const posUserBtnRef = useRef(null);
  const managerBtnRef = useRef(null);
  const { printUSB } = usePrint();

  // ── Printer ──────────────────────────────────────────────
  const [printerStatus, setPrinterStatus] = useState("ATTENTION");
  const [printerStatusText, setPrinterStatusText] = useState("Click Check to run a test print");
  const [isPrinting, setIsPrinting] = useState(false);
  const [printerChecked, setPrinterChecked] = useState(false);

  const handlePrinterCheck = useCallback(async () => {
    setIsPrinting(true);
    setPrinterStatusText("Sending test print…");
    try {
      await printUSB(false, true, undefined, undefined, true);
      setPrinterStatusText("Test print sent · Check the paper output");
      setPrinterStatus("ATTENTION");
    } catch {
      setPrinterStatusText("Test print failed · Check connection");
      setPrinterStatus("FAILED");
      showSnackbar("Printing Failed", "error");
    } finally {
      setIsPrinting(false);
      setPrinterChecked(true);
    }
  }, [printUSB]);

  const handleMarkPrinterReady = () => {
    setPrinterStatus("READY");
    setPrinterStatusText("Paper loaded · Printer confirmed ready");
  };

  // ── Network ───────────────────────────────────────────────
  const [networkStatus, setNetworkStatus] = useState("ATTENTION");
  const [networkStatusText, setNetworkStatusText] = useState("Click Check to test network speed");
  const [isCheckingNetwork, setIsCheckingNetwork] = useState(false);
  const [networkChecked, setNetworkChecked] = useState(false);

  const handleNetworkCheck = useCallback(async () => {
    setIsCheckingNetwork(true);
    setNetworkStatusText("Measuring connection…");
    try {
      if (!navigator.onLine) {
        setNetworkStatusText("No internet connection detected");
        setNetworkStatus("ATTENTION");
        return;
      }
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      const pingStart = performance.now();
      try {
        await fetch(`${window.location.origin}/favicon.ico?_=${Date.now()}`, {
          cache: "no-store",
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timer);
      }
      const rtt = Math.round(performance.now() - pingStart);
      const downlink = conn?.downlink ?? null;
      const effectiveType = conn?.effectiveType ?? null;
      if (downlink != null && downlink > 0) {
        const quality = downlink >= 5 ? "Excellent" : downlink >= 2 ? "Good" : "Weak";
        setNetworkStatusText(
          `${quality} · ${downlink} Mbps · ${rtt}ms ping${
            effectiveType ? ` · ${effectiveType.toUpperCase()}` : ""
          }`
        );
        setNetworkStatus(downlink >= 1 ? "READY" : "ATTENTION");
      } else {
        const quality = rtt < 150 ? "Excellent" : rtt < 400 ? "Good" : "Weak";
        setNetworkStatusText(`${quality} · ${rtt}ms latency · LAN sync confirmed`);
        setNetworkStatus(rtt < 1000 ? "READY" : "ATTENTION");
      }
    } catch (err) {
      if (err?.name === "AbortError") {
        setNetworkStatusText("Request timed out · Check LAN or server status");
      } else {
        setNetworkStatusText("Unable to reach server · Check LAN connection");
      }
      setNetworkStatus("ATTENTION");
    } finally {
      setIsCheckingNetwork(false);
      setNetworkChecked(true);
    }
  }, []);

  const handleMarkNetworkReady = () => {
    setNetworkStatus("READY");
    setNetworkStatusText("Manually confirmed · Network marked as ready");
  };

  // ── Buzzer ────────────────────────────────────────────────
  const [buzzerStatus, setBuzzerStatus] = useState("ATTENTION");
  const [buzzerStatusText, setBuzzerStatusText] = useState(
    "Click Check to ring the buzzer for 3 seconds"
  );
  const [isBuzzing, setIsBuzzing] = useState(false);
  const [buzzerChecked, setBuzzerChecked] = useState(false);

  const handleBuzzerCheck = useCallback(() => {
    if (isBuzzing) return;
    setIsBuzzing(true);
    setBuzzerStatusText("Buzzer ringing…");
    const audio = new Audio(AlertAudio);
    audio.loop = true;
    audio.play().catch(() => {
      setBuzzerStatusText("Browser blocked audio · Click Check again after any interaction");
      setIsBuzzing(false);
    });
    setTimeout(() => {
      audio.pause();
      audio.currentTime = 0;
      setBuzzerStatusText("Buzzer rang for 3 seconds · Mark as ready if sound was heard");
      setIsBuzzing(false);
      setBuzzerChecked(true);
    }, 3000);
  }, [isBuzzing]);

  const handleMarkBuzzerReady = () => {
    setBuzzerStatus("READY");
    setBuzzerStatusText("Buzzer confirmed · Alert sound working");
  };

  // ── EDC ───────────────────────────────────────────────────
  const [edcStatus, setEdcStatus] = useState("ATTENTION");
  const [edcStatusText, setEdcStatusText] = useState("Click Check to verify card terminal link");
  const [isCheckingEdc, setIsCheckingEdc] = useState(false);
  const [edcChecked, setEdcChecked] = useState(false);

  const handleEdcCheck = useCallback(async () => {
    setIsCheckingEdc(true);
    setEdcStatusText("Verifying card terminal…");

    const payload = {
      licenseId: localStorage.getItem("selectedLicenseId"),
    };

    const response = await getMachineDetails(payload);

    const machineData = response?.data?.data?.data;

    if (machineData?.machineResponses?.length > 0) {
      showSnackbar("Machine Connected", "success");
      setEdcStatusText("Check complete · Review and mark ready");
      setIsCheckingEdc(false);
      setEdcChecked(true);
    } else {
      showSnackbar("No machines found", "warning");
      setIsCheckingEdc(false);
      setEdcChecked(false);
    }
  }, []);

  const handleMarkEdcReady = () => {
    setEdcStatus("READY");
    setEdcStatusText("Card terminal confirmed · EDC link ready");
  };

  // ── Keyboard nav ──────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        managerBtnRef.current?.focus();
        setFocusedButton("manager");
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        posUserBtnRef.current?.focus();
        setFocusedButton("pos");
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (focusedButton === "manager") handleManagerLogin();
        else handleSubmit();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [focusedButton]);

  const handlePin = () => navigate("/session-page/authentication");

  const handleManagerLogin = () => {
    if (isPosManager) {
      sessionCreationManager();
    } else {
      showSnackbar("You are not a store manager!", "error");
    }
  };

  const handlevalueChange = (e) => {
    dispatch(addadvance(e.target.value));
    setAmountvalue(e.target.value);
  };

  const handleSubmit = () => {
    if (isPosManager) {
      sessionCreationManager();
    } else {
      handlePin();
    }
  };

  const goToHomepage = () => {
    const retailType = localStorage.getItem("retailType");
    if (retailType === "RESTAURANT") {
      tableLayoutAvailability(contextId)
        .then((res) => {
          localStorage.setItem("layoutId", res?.data?.object?.[0]?.layoutId);
          return getInventoryPosConfig(orgId, contextId);
        })
        .then((configRes) => {
          if (configRes?.data) {
            localStorage.setItem("isInventoryConfigSet", configRes?.data?.data?.es === 0);
            const enable = configRes?.data?.data?.data?.enableMandatoryStockCount;
            if (enable !== undefined)
              localStorage.setItem("enableMandatoryStockCount", enable.toString());
          }
        })
        .catch(() => {})
        .finally(() => navigate("/dashboard"));
    } else {
      navigate("/dashboard");
    }
  };

  const sessionCreationManager = () => {
    setManagerLoader(true);
    const licenseId = selectedLicense?.licenseId || localStorage.getItem("selectedLicenseId") || "";
    const licenseName =
      selectedLicense?.licenseName || localStorage.getItem("selectedLicenseName") || "";
    const payload = {
      uidx,
      role: "CASHIER",
      organizationId: orgId,
      locationId: contextId,
      licenseName,
      licenseId,
      cashDrawerStartingAmount: amount === "" ? "0" : amount,
      createdBy: "manager",
      createdByName: userName,
      posType: isMobile ? "MPOS" : "POS",
    };
    createSession(payload)
      .then((res) => {
        if (res?.data?.es === 1) {
          showSnackbar(res?.data?.message + ". Please retry again", "error");
          setManagerLoader(false);
          dispatch(addLicense({ licenseId: "", licenseName: "" }));
          dispatch(addadvance(""));
          dispatch(addpin(""));
          dispatch(addmanagerpin(""));
          dispatch(addmanageruidx(""));
        } else {
          showSnackbar(res?.data?.message, "success");
          getActiveSession(uidx, contextId, daySessionId)
            .then((res) => {
              if (res?.data?.statusCode !== 300) {
                if (res?.data?.hasOwnProperty("machines")) {
                  localStorage.setItem("machineId", res?.data?.machines?.[0].machineId || 0);
                  if (
                    localStorage.getItem("loggedInCount") === null ||
                    localStorage.getItem("loggedInCount") === undefined
                  ) {
                    localStorage.setItem("storeMode", "ONLINE");
                    localStorage.setItem("cartStoreMode", "ONLINE");
                    localStorage.setItem("loggedInCount", 1);
                  }
                }
                localStorage.setItem("sessionId", res?.data?.userSession?.sessionId);
                localStorage.setItem("licenseId", res?.data?.userSession?.licenseId);
                setInLocalStorage("licenseName", res?.data?.userSession?.licenseName);
              }
              goToHomepage();
            })
            .catch(() => goToHomepage());
        }
      })
      .catch((err) => {
        showSnackbar(err?.response?.data?.message || err?.message, "error");
        setManagerLoader(false);
      });
  };

  const handleLogout = () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    const payload = {
      metaData: {
        os: navigator.platform,
        latitude: "",
        browser: window.navigator.userAgent,
        city: "",
        country: "",
        postCode: "",
        longitude: "",
        device: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Windows Phone/i.test(
          navigator.userAgent
        )
          ? "This device is Mobile"
          : /Windows/i.test(navigator.userAgent)
          ? "This device is Desktop"
          : "This device is tablet",
      },
    };

    logoutSession(payload)
      .then((res) => {
        showSnackbar("Successfully Logged out", "success");
        isMobilePos && emit({ type: "localData", data: null });
        Object.keys(localStorage).forEach((key) => {
          if (key !== "printerConfig") {
            localStorage.removeItem(key);
          }
        });
        navigate("/");
        setIsLoggingOut(false);
      })
      .catch((err) => {
        showSnackbar(err.message || "Logout failed", "error");
        isMobilePos && emit({ type: "localData", data: null });
        Object.keys(localStorage).forEach((key) => {
          if (key !== "printerConfig") {
            localStorage.removeItem(key);
          }
        });
        setIsLoggingOut(false);
      });
  };

  const terminalName =
    selectedLicense?.licenseName || localStorage.getItem("selectedLicenseName") || "Terminal";

  // ── Reusable check card action renderer ───────────────────
  const renderCardAction = ({ status, checked, isLoading, onCheck, onMarkReady, loadingLabel }) => {
    if (status === "READY") {
      return (
        <div className="op-check-confirmed">
          <CheckCircleIcon sx={{ fontSize: 14 }} /> Confirmed ready
        </div>
      );
    }
    if (checked) {
      return (
        <div className="op-checked-actions">
          <button className="op-retry-btn" onClick={onCheck} disabled={isLoading} title="Retry">
            {isLoading ? (
              <CircularProgress size={12} sx={{ color: "#374151" }} />
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-refresh-ccw h-4 w-4"
                aria-hidden="true"
              >
                <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                <path d="M3 3v5h5"></path>
                <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path>
                <path d="M16 16h5v5"></path>
              </svg>
            )}
          </button>
          <button className="op-mark-ready-btn" onClick={onMarkReady} disabled={isLoading || status === "FAILED"}>
            <CheckCircleIcon sx={{ fontSize: 14 }} /> Mark as ready
          </button>
        </div>
      );
    }
    return (
      <button className="op-check-btn" onClick={onCheck} disabled={isLoading}>
        {isLoading ? (
          <>
            <CircularProgress size={12} sx={{ color: "#374151" }} /> {loadingLabel}
          </>
        ) : (
          <>
            Check{" "}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-refresh-ccw ml-2 h-4 w-4"
              aria-hidden="true"
            >
              <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
              <path d="M3 3v5h5"></path>
              <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"></path>
              <path d="M16 16h5v5"></path>
            </svg>
          </>
        )}
      </button>
    );
  };

  return (
    <>
      {isLoggingOut && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            cursor: "not-allowed",
          }}
        />
      )}
      <div className="op-page" style={{ opacity: isLoggingOut ? 0.8 : 1 }}>
        <div className="op-header-card">
          {/* ── Header ── */}
          <div className="op-header">
            <section className="op-header-left-section">
              <button
                className="back-btn"
                data-testid="button-system-health-back"
                onClick={() => navigate("/session-page/start-day")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  className="back-icon"
                >
                  <path d="m12 19-7-7 7-7" />
                  <path d="M19 12H5" />
                </svg>
              </button>
              <img src={PalletLogo} alt="Pallet" className="op-logo" />
            </section>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <span className="op-step-badge">Step 4 of 4</span>
              <div
                onClick={handleLogout}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: isLoggingOut ? "not-allowed" : "pointer",
                  color: "#e53e3e",
                }}
                title="Logout"
              >
                {isLoggingOut ? (
                  <ArrowPathIcon className="spinner" style={{ width: 20, height: 20 }} />
                ) : (
                  <LogoutIcon sx={{ fontSize: "1.25rem" }} />
                )}
                <span style={{ fontSize: "0.875rem", fontWeight: 600 }}>Logout</span>
              </div>
            </div>
          </div>

        </div>{/* end op-header-card */}
          {/* ── Body ── */}
          <div className="op-body">
            {/* Title + breadcrumbs */}
            <div className="op-setup-section">
              <div className="op-setup-header">
                <div>
                  <h2 className="op-title" data-testid="text-system-health-title">
                    System health
                  </h2>
                </div>
                <div className="op-breadcrumbs">
                  <span className="op-crumb" data-testid="text-system-health-location">
                    {locName}
                  </span>
                  <span className="op-crumb" data-testid="text-system-health-terminal-name">
                    {terminalName}
                  </span>

                </div>
              </div>
            </div>

            {/* System health checks */}
            <div className="op-section-body">
              <div className="op-checks-grid">
                {/* Printer */}
                <div
                  className={`op-check-card ${
                    printerStatus === "READY"
                      ? "ready"
                      : printerStatus === "FAILED"
                      ? "failed"
                      : "attention"
                  }`}
                  data-testid="card-system-health-check-printer"
                >
                  <div className="op-check-top">
                    <div
                      className={`op-check-icon ${
                        printerStatus === "READY"
                          ? "ready"
                          : printerStatus === "FAILED"
                          ? "failed"
                          : "attention"
                      }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-printer h-5 w-5"
                        aria-hidden="true"
                      >
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6"></path>
                        <rect x="6" y="14" width="12" height="8" rx="1"></rect>
                      </svg>
                    </div>
                    <span
                      className={`op-check-status ${
                        printerStatus === "READY"
                          ? "ready"
                          : printerStatus === "FAILED"
                          ? "failed"
                          : "attention"
                      }`}
                      data-testid="status-system-health-check-printer"
                    >
                      {printerStatus === "READY"
                        ? "Ready"
                        : printerStatus === "FAILED"
                        ? "Failed"
                        : "Attention"}
                    </span>
                  </div>
                  <p className="op-check-name" data-testid="text-system-health-check-title-printer">
                    Receipt printer
                  </p>
                  <p className="op-check-desc">Primary bill printer</p>
                  <div className="op-check-info">{printerStatusText}</div>
                  <div className="op-check-card-actions">
                    {renderCardAction({
                      status: printerStatus,
                      checked: printerChecked,
                      isLoading: isPrinting,
                      onCheck: handlePrinterCheck,
                      onMarkReady: handleMarkPrinterReady,
                      loadingLabel: "Printing…",
                    })}
                  </div>
                </div>

                {/* EDC */}
                <div
                  className={`op-check-card ${edcStatus === "READY" ? "ready" : "attention"}`}
                  data-testid="card-system-health-check-edc"
                >
                  <div className="op-check-top">
                    <div
                      className={`op-check-icon ${edcStatus === "READY" ? "ready" : "attention"}`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-credit-card h-5 w-5"
                        aria-hidden="true"
                      >
                        <rect width="20" height="14" x="2" y="5" rx="2"></rect>
                        <line x1="2" x2="22" y1="10" y2="10"></line>
                      </svg>
                    </div>
                    <span
                      className={`op-check-status ${edcStatus === "READY" ? "ready" : "attention"}`}
                      data-testid="status-system-health-check-edc"
                    >
                      {edcStatus === "READY" ? "Ready" : "Attention"}
                    </span>
                  </div>
                  <p className="op-check-name" data-testid="text-system-health-check-title-edc">
                    EDC machine
                  </p>
                  <p className="op-check-desc">Card terminal link</p>
                  <div className="op-check-info">{edcStatusText}</div>
                  <div className="op-check-card-actions">
                    {renderCardAction({
                      status: edcStatus,
                      checked: edcChecked,
                      isLoading: isCheckingEdc,
                      onCheck: handleEdcCheck,
                      onMarkReady: handleMarkEdcReady,
                      loadingLabel: "Checking…",
                    })}
                  </div>
                </div>

                {/* Network */}
                <div
                  className={`op-check-card ${networkStatus === "READY" ? "ready" : "attention"}`}
                  data-testid="card-system-health-check-network"
                >
                  <div className="op-check-top">
                    <div
                      className={`op-check-icon ${
                        networkStatus === "READY" ? "ready" : "attention"
                      }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-wifi h-5 w-5"
                        aria-hidden="true"
                      >
                        <path d="M12 20h.01"></path>
                        <path d="M2 8.82a15 15 0 0 1 20 0"></path>
                        <path d="M5 12.859a10 10 0 0 1 14 0"></path>
                        <path d="M8.5 16.429a5 5 0 0 1 7 0"></path>
                      </svg>
                    </div>
                    <span
                      className={`op-check-status ${
                        networkStatus === "READY" ? "ready" : "attention"
                      }`}
                      data-testid="status-system-health-check-network"
                    >
                      {networkStatus === "READY" ? "Ready" : "Attention"}
                    </span>
                  </div>
                  <p className="op-check-name" data-testid="text-system-health-check-title-network">
                    Counter network
                  </p>
                  <p className="op-check-desc">LAN and cloud sync</p>
                  <div className="op-check-info">{networkStatusText}</div>
                  <div className="op-check-card-actions">
                    {renderCardAction({
                      status: networkStatus,
                      checked: networkChecked,
                      isLoading: isCheckingNetwork,
                      onCheck: handleNetworkCheck,
                      onMarkReady: handleMarkNetworkReady,
                      loadingLabel: "Checking…",
                    })}
                  </div>
                </div>

                {/* Buzzer */}
                <div
                  className={`op-check-card ${buzzerStatus === "READY" ? "ready" : "attention"}`}
                  data-testid="card-system-health-check-buzzer"
                >
                  <div className="op-check-top">
                    <div
                      className={`op-check-icon ${
                        buzzerStatus === "READY" ? "ready" : "attention"
                      }`}
                    >
                      <VolumeUpIcon sx={{ fontSize: 20 }} />
                    </div>
                    <span
                      className={`op-check-status ${
                        buzzerStatus === "READY" ? "ready" : "attention"
                      }`}
                      data-testid="status-system-health-check-buzzer"
                    >
                      {buzzerStatus === "READY" ? "Ready" : "Attention"}
                    </span>
                  </div>
                  <p className="op-check-name" data-testid="text-system-health-check-title-buzzer">
                    Alert buzzer
                  </p>
                  <p className="op-check-desc">ODS order notification sound</p>
                  <div className="op-check-info">{buzzerStatusText}</div>
                  <div className="op-check-card-actions">
                    {renderCardAction({
                      status: buzzerStatus,
                      checked: buzzerChecked,
                      isLoading: isBuzzing,
                      onCheck: handleBuzzerCheck,
                      onMarkReady: handleMarkBuzzerReady,
                      loadingLabel: "Ringing…",
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Opening cash */}
            <div className="op-cash-section">
              <p className="op-section-tag">Opening cash</p>
              <div className="op-cash-row">
                {/* Left: label + input */}
                <div className="op-cash-left">
                  <p className="op-cash-desc" data-testid="text-system-health-opening-cash-copy">
                    Enter the drawer amount for <strong>{terminalName}</strong> before continuing.
                  </p>
                  <div className="op-amount-wrap">
                    <span className="op-rupee">{getStoreCurrencySymbol()}</span>
                    <input
                      className="op-amount-input"
                      type="number"
                      placeholder="Enter opening cash"
                      autoFocus
                      data-testid="input-system-health-opening-cash"
                      onChange={handlevalueChange}
                      onKeyDown={(e) => {
                        if (
                          [
                            "-",
                            "+",
                            "*",
                            "/",
                            "e",
                            "E",
                            ".",
                            "ArrowUp",
                            "ArrowDown",
                            "ArrowLeft",
                            "ArrowRight",
                          ].includes(e.key)
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onWheel={(e) => e.target.blur()}
                    />
                  </div>
                </div>

                {/* Right: CTA button */}
                <div className="op-cash-btns">
                  <button
                    ref={posUserBtnRef}
                    className="op-btn pos-user"
                    onClick={handleSubmit}
                    disabled={managerLoader || loader}
                    data-testid="button-system-health-pos-action"
                  >
                    {loader || managerLoader ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : (
                      <>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <polygon points="5 3 19 12 5 21 5 3" />
                        </svg>
                        Start session
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
      </div>
    </>
  );
};
